import React from 'react';

import { useTheme } from 'styled-components';
import { ThemeType } from 'theme/theme';

import { ReactComponent as ExpandMoreIcon } from 'assets/images/expand-more.svg';
import { ReactComponent as BackpackActive } from 'assets/images/luggages/backpack-active.svg';
import { ReactComponent as HandLuggageActive } from 'assets/images/luggages/hand-luggage-active.svg';
import { ReactComponent as HandLuggage } from 'assets/images/luggages/hand-luggage.svg';
import { ReactComponent as WarehouseLuggageActive } from 'assets/images/luggages/warehouse-luggage-active.svg';
import { ReactComponent as WarehouseLuggage } from 'assets/images/luggages/warehouse-luggage.svg';
import Flightline from 'components/FlightLine';
import useBoolean from 'hooks/useBoolean';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { GroupedSectorOptions } from 'models/flightSearch';

import { formatDuration, getTime, getTurnDays } from '../../utils';
import CollapsibleDetail from '../CollapsibleDetail';
import {
  AirlineLogo,
  AirlineName,
  AirlineRadioWrapper,
  ExpandMoreButton,
  FlightContainer,
  FlightLineWrapper,
  FlightLuggages,
  FlightLuggagesContainer,
  FlightTimeArrivalWrapper,
  FlightTimeText,
  FormControlLabel,
  RadioButton,
  TurnDays,
  TooltipContainer,
  AirportCodeText,
  Restrictions
} from './styles';
import Tooltip from 'components/Tooltips/LuggagesTooltip';
import { List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';

interface Props {
  groupedSectorOption: GroupedSectorOptions;
}

const FlightOptionDesktop = ({ groupedSectorOption }: Props) => {
  const { t } = useStoreTranslation();
  const showDetail = useBoolean(false);
  const { flights, totalDuration, flightKeys } = groupedSectorOption;
  const numberOfStops = flights.length - 1;
  const departureFlight = flights.first();
  const arrivalFlight = flights.last();
  const turnDays = getTurnDays(departureFlight.departureDate, arrivalFlight.arrivalDate);
  const theme = useTheme() as ThemeType;

  const haveWarehouseBaggage = !departureFlight.baggageRestrictions?.startsWith('0');
  const restrictions = departureFlight.baggageRestrictions;

  return (
    <>
      <FlightContainer>
        <AirlineRadioWrapper>
          <FormControlLabel
            value={flightKeys}
            control={<RadioButton disableRipple color="default" />}
            label={undefined}
          />
          <AirlineLogo airline={departureFlight.airlineCode} />
          <AirlineName>{departureFlight.airlineName}</AirlineName>
        </AirlineRadioWrapper>
        <FlightTimeText>{getTime(departureFlight.departureDate)}<AirportCodeText>{departureFlight.departureAirport}</AirportCodeText></FlightTimeText>              
        <FlightLineWrapper>
          <Flightline
            points={numberOfStops}
            topText={formatDuration(totalDuration)}
            bottomText={
              numberOfStops === 0 ? t('flightsCard.direct') : t('flightsCard.stopNumber', { count: numberOfStops })
            }
          />
        </FlightLineWrapper>
        <FlightTimeArrivalWrapper>
          {turnDays > 0 && <TurnDays>{`+${turnDays}`}</TurnDays>}
          <FlightTimeText>{getTime(arrivalFlight.arrivalDate)}<AirportCodeText>{arrivalFlight.arrivalAirport}</AirportCodeText></FlightTimeText>
        </FlightTimeArrivalWrapper>
        <div />
        <FlightLuggagesContainer>
          <Tooltip
            event="hover"
            content={
              <TooltipContainer>
                <p>{t('flightsCard.luggage')}</p>
                <List dense disablePadding>
                  <ListItem>
                    <ListItemIcon>
                      <BackpackActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
                    </ListItemIcon>
                    <ListItemText primary={t('luggageInfo.personalArticle')} />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {departureFlight.handBaggage ? (
                        <HandLuggageActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
                      ) : (
                        <HandLuggage />
                      )}
                    </ListItemIcon>
                    {departureFlight.handBaggage ? (
                      <ListItemText primary={t('luggageInfo.handBaggage')} />
                    ) : (
                      <ListItemText primary={t('luggageInfo.NoHandBaggage')} />
                    )}
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      {departureFlight.baggageRestrictions && departureFlight.baggageRestrictions.startsWith('0') ? (
                        <WarehouseLuggage />
                      ) : (
                        <WarehouseLuggageActive
                          fill={theme.primaryBackgroundColor}
                          stroke={theme.primaryBackgroundColor}
                        />
                      )}
                    </ListItemIcon>
                    {departureFlight.baggageRestrictions && departureFlight.baggageRestrictions.startsWith('0') ? (
                        <ListItemText primary={t('luggageInfo.NoCheckedBaggage')} />
                      ) : (
                        <ListItemText primary={t('luggageInfo.checkedBaggage')} />                        
                      )}
                  </ListItem>
                  {haveWarehouseBaggage && (
                    <ListItem>
                      <ListItemIcon>
                        <Restrictions>{t('flightSectorBaggage.restriction', {
                          restrictions: restrictions,
                        })}
                        </Restrictions>                        
                      </ListItemIcon>
                      <ListItemText primary='' />
                    </ListItem>
                  )}
                </List>
              </TooltipContainer>
            }
          >
            <FlightLuggages>
              <BackpackActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
              {departureFlight.handBaggage ? (
                <HandLuggageActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
              ) : (
                <HandLuggage />
              )}
              {departureFlight.baggageRestrictions && departureFlight.baggageRestrictions.startsWith('0') ? (
                <WarehouseLuggage />
              ) : (
                <WarehouseLuggageActive fill={theme.primaryBackgroundColor} stroke={theme.primaryBackgroundColor} />
              )}
            </FlightLuggages>
          </Tooltip>

          <ExpandMoreButton rotated={showDetail.value} onClick={showDetail.toggle}>
            <ExpandMoreIcon />
          </ExpandMoreButton>
        </FlightLuggagesContainer>
      </FlightContainer>
      <CollapsibleDetail expanded={showDetail.value} flights={flights} totalDuration={totalDuration} />
    </>
  );
};

export default FlightOptionDesktop;
